import {
    useContext,
    useEffect,
    useState,
    type FC
} from "react";
import {
    Platform,
    View
} from "react-native";
import type IMainHeaderProps from "./type";
import type {
    MainHeaderCenterOverflowType
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../types";
import {
    useBreakpoint
} from "../../helpers/pointer";
import {
    Menu as MenuIcon,
    X as XIcon
} from "lucide-react-native";
import {
    SafeAreaView
} from "react-native-safe-area-context";
import {
    warnOnce
} from "../../utils/development";
import {
    Portal
} from "../../helpers/portalize";
import {
    SheetContext
} from "../sheet/context";
import AnimatedIcon from "../animatedIcon";
import Separator from "../separator";
import SiteLogo from "../siteLogo";
import Button from "../button";
import Sheet from "../sheet";

const MainHeaderMenuIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => {
    const sheet = useContext(SheetContext);

    return <AnimatedIcon
        isTransitioned={sheet?.isOpen ?? false}
        customColor={customColor}
        iconTransition="morph"
        fromIcon={MenuIcon}
        toIcon={XIcon}
        color={color}
        size={size}
    />;
};

const MainHeader: FC<IMainHeaderProps> = ({
    renderSwitchers: renderSwitchersProp,
    renderCenter: renderCenterProp,
    isWorkWithSafeAreaView = true,
    isWorkWithStickySpace = false,
    renderRight: renderRightProp,
    backgroundColor = "default",
    isSetAutoHeaderSpace = true,
    portalName = "main-header",
    renderLeft: renderLeftProp,
    renderMenu: renderMenuProp,
    isWorkWithPortal = false,
    isWorkWithSticky = false,
    customBackgroundColor,
    isWorkWithSeparator,
    isWorkWithSeperator,
    onContentHeight,
    customLocalize,
    siteLogoProps,
    customTheme,
    glassEffect,
    isScrolled,
    menuTitle,
    children,
    style,
    ...props
}) => {
    const {
        controlHeights,
        shapeConfigs,
        shadowAlpha,
        elevations,
        iconSizes,
        borders,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const breakpoint = useBreakpoint();

    const isCurrentWorkWithSeparator = isWorkWithSeparator ?? isWorkWithSeperator;

    const isMenuAvailable = renderMenuProp !== undefined || renderSwitchersProp !== undefined;

    const [
        containerWidth,
        setContainerWidth
    ] = useState<number>(0);

    const [
        centerWidth,
        setCenterWidth
    ] = useState<number>(0);

    const [
        centerNaturalWidth,
        setCenterNaturalWidth
    ] = useState<number>(0);

    const [
        centerOverflow,
        setCenterOverflow
    ] = useState<null | MainHeaderCenterOverflowType>(null);

    const isCompactMenu = isMenuAvailable && (Platform.OS !== "web" || breakpoint === "compact" || centerOverflow !== null);

    const [
        isMenuOpen,
        setIsMenuOpen
    ] = useState<boolean>(false);

    const [
        contentHeight,
        setContentHeight
    ] = useState<null | number>(null);

    const {
        stickyContainer: stickyContainerDynamicStyle,
        safeAreaView: safeAreaViewDynamicStyle,
        stickySpacer: stickySpacerDynamicStyle,
        container: containerDynamicStyle,
        separator: separatorDynamicStyle,
        switchers: switchersDynamicStyle,
        menuBody: menuBodyDynamicStyle,
        actions: actionsDynamicStyle
    } = useStyles({
        isWorkWithSeparator: isCurrentWorkWithSeparator,
        customBackgroundColor,
        backgroundColor,
        controlHeights,
        contentHeight,
        shapeConfigs,
        shadowAlpha,
        breakpoint,
        elevations,
        isScrolled,
        borders,
        spaces,
        colors
    });

    useEffect(() => {
        if(!isCompactMenu) {
            setIsMenuOpen(false);
        }
    }, [
        isCompactMenu
    ]);

    useEffect(() => {
        if(!isMenuAvailable || centerOverflow || centerWidth <= 0 || centerNaturalWidth <= centerWidth) {
            return;
        }

        setCenterOverflow({
            deficit: centerNaturalWidth - centerWidth,
            width: containerWidth
        });
    }, [
        centerNaturalWidth,
        isMenuAvailable,
        containerWidth,
        centerOverflow,
        centerWidth
    ]);

    useEffect(() => {
        if(!centerOverflow || containerWidth < centerOverflow.width + centerOverflow.deficit + 1) {
            return;
        }

        setCenterOverflow(null);
    }, [
        centerOverflow,
        containerWidth
    ]);

    useEffect(() => {
        if(onContentHeight) onContentHeight(contentHeight);
    }, [
        contentHeight
    ]);

    useEffect(() => {
        if(isWorkWithSeperator === undefined) {
            return;
        }

        warnOnce({
            message: "MainHeader: \"isWorkWithSeperator\" is deprecated and will be removed in the next major version. Use \"isWorkWithSeparator\" instead."
        });
    }, [
        isWorkWithSeperator
    ]);

    useEffect(() => {
        if(glassEffect === undefined) {
            return;
        }

        warnOnce({
            message: "MainHeader: \"glassEffect\" is deprecated and has no effect, because the bar is opaque now. It will be removed in the next major version."
        });
    }, [
        glassEffect
    ]);

    useEffect(() => {
        if(!isWorkWithSticky || !isSetAutoHeaderSpace) {
            return;
        }

        return () => {
            NCoreUIKitTheme.updateConfigs({
                headerSpace: 0
            });
        };
    }, [
        isSetAutoHeaderSpace,
        isWorkWithSticky
    ]);

    const renderRight = () => {
        if(renderRightProp) {
            return renderRightProp();
        }

        return <View/>;
    };

    const handleMenuOpenChange = (isOpen: boolean) => {
        setIsMenuOpen(isOpen);
    };

    const handleMenuToggle = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const renderCenter = () => {
        if(!renderCenterProp || isCompactMenu) {
            return null;
        }

        return <View
            onLayout={(event) => setCenterWidth(event.nativeEvent.layout.width)}
            style={[
                stylesheet.center
            ]}
        >
            <View
                onLayout={(event) => setCenterNaturalWidth(event.nativeEvent.layout.width)}
                style={stylesheet.centerContent}
            >
                {renderCenterProp()}
            </View>
        </View>;
    };

    const renderMenuButton = () => {
        if(!isCompactMenu) {
            return null;
        }

        return <Sheet
            onOpenChange={handleMenuOpenChange}
            customLocalize={customLocalize}
            compactBehaviour="keepSide"
            customTheme={customTheme}
            isOpen={isMenuOpen}
            side="top"
        >
            <Button
                accessibilityLabel={localize(isMenuOpen ? "close-menu" : "open-menu")}
                onPress={handleMenuToggle}
                aria-expanded={isMenuOpen}
                icon={MainHeaderMenuIcon}
                customTheme={customTheme}
                aria-haspopup="dialog"
                variant="ghost"
                type="neutral"
            />
            <Sheet.Content
                aria-label={menuTitle ?? localize("menu")}
            >
                <Sheet.Header
                    title={menuTitle ?? localize("menu")}
                />
                <Sheet.Body>
                    <View
                        style={[
                            stylesheet.menuBody,
                            menuBodyDynamicStyle
                        ]}
                    >
                        {renderMenuProp ? renderMenuProp() : null}
                        {
                            renderSwitchersProp ?
                                <View
                                    style={[
                                        stylesheet.switchers,
                                        stylesheet.menuSwitchers,
                                        switchersDynamicStyle
                                    ]}
                                >
                                    {renderSwitchersProp()}
                                </View>
                            :
                                null
                        }
                    </View>
                </Sheet.Body>
            </Sheet.Content>
        </Sheet>;
    };

    const renderEnd = () => {
        if(!renderSwitchersProp && !isMenuAvailable) {
            return renderRight();
        }

        return <View
            style={[
                stylesheet.actions,
                actionsDynamicStyle
            ]}
        >
            {
                renderSwitchersProp && !isCompactMenu ?
                    <View
                        style={[
                            stylesheet.switchers,
                            switchersDynamicStyle
                        ]}
                    >
                        {renderSwitchersProp()}
                    </View>
                :
                    null
            }
            {
                renderSwitchersProp && !isCompactMenu && renderRightProp ?
                    <Separator
                        style={[
                            separatorDynamicStyle
                        ]}
                        length={iconSizes.medium}
                        customTheme={customTheme}
                        orientation="vertical"
                        aria-hidden={true}
                    />
                :
                    null
            }
            {renderRightProp ? renderRightProp() : null}
            {renderMenuButton()}
        </View>;
    };

    const renderLeft = () => {
        if(renderLeftProp) {
            return renderLeftProp();
        }

        return <SiteLogo
            customLocalize={customLocalize}
            style={stylesheet.siteLogo}
            customTheme={customTheme}
            isWorkWithFlex1={false}
            size="medium"
            {...siteLogoProps}
        />;
    };

    const renderContent = () => {
        return <View
            {...props}
            style={[
                style,
                stylesheet.container,
                isWorkWithSticky && !isWorkWithSafeAreaView ? stylesheet.stickyContainer : undefined,
                containerDynamicStyle,
                isWorkWithSticky && !isWorkWithSafeAreaView ? stickyContainerDynamicStyle : undefined
            ]}
            onLayout={(event) => {
                if(props && props.onLayout) {
                    props.onLayout(event);
                }

                setContainerWidth(event.nativeEvent.layout.width);

                if(isWorkWithSticky) {
                    const _contentHeight = event.nativeEvent.layout.height;

                    setContentHeight(_contentHeight);

                    if(isSetAutoHeaderSpace) {
                        NCoreUIKitTheme.updateConfigs({
                            headerSpace: _contentHeight
                        });
                    }
                }
            }}
        >
            {renderLeft()}
            {renderCenter()}
            {renderEnd()}
        </View>;
    };

    const renderContentContainer = () => {
        if(isWorkWithPortal) {
            return <Portal
                name={portalName}
            >
                {renderContent()}
            </Portal>;
        }

        return renderContent();
    };

    const renderSafeAreaView = () => {
        return <SafeAreaView
            style={[
                isWorkWithSticky ? stylesheet.stickyContainer : undefined,
                safeAreaViewDynamicStyle,
                isWorkWithSticky ? stickyContainerDynamicStyle : undefined
            ]}
            edges={[
                "top"
            ]}
        >
            {renderContentContainer()}
        </SafeAreaView>;
    };

    const renderWithChildren = () => {
        return <View
            style={[
                stylesheet.baseContainer
            ]}
        >
            {isWorkWithSafeAreaView ? renderSafeAreaView() : renderContentContainer()}
            {
                isWorkWithSticky && isWorkWithStickySpace ?
                    <View
                        style={[
                            stylesheet.stickySpacer,
                            stickySpacerDynamicStyle
                        ]}
                    />
                :
                    null
            }
            {children}
        </View>;
    };

    if(children) {
        return renderWithChildren();
    }

    return isWorkWithSafeAreaView ? renderSafeAreaView() : renderContentContainer();
};
export default MainHeader;
